<template>
  <section class="co-page form-page">
    <router-link :to="editId ? { name: 'coreanos-detail', params: { id: editId } } : { name: 'coreanos' }" class="back">
      <b-icon icon="arrow-left" size="is-small" />{{ editId ? "Voltar ao pedido" : "Pedidos" }}
    </router-link>
    <h1 class="co-title">{{ editId ? "Editar pedido" : "Novo pedido" }}</h1>
    <p class="lead">
      {{ isLiveEdit ? "As alterações são enviadas ao grupo e, em privado, aos guias da equipa." : "Preenche o essencial. Os guias só veem a data, o percurso, os tuk-tuks e o pagamento." }}
    </p>

    <div v-if="loading" class="co-card loading"><b-loading :is-full-page="false" :active="true" /></div>

    <div v-else class="co-layout">
      <form ref="form" class="main" novalidate @submit.prevent="submit(primaryPublishes)">
        <!-- Only when it matters: the team will have to reconfirm -->
        <div v-if="needsReconfirm" class="callout is-warn" role="status">
          <b-icon icon="exclamation-triangle" size="is-small" />
          <p>
            <strong>Mudaste a data ou a hora.</strong> Os {{ assignedCount }} guias da equipa vão ter de reconfirmar em 15 min no Telegram. Quem não
            responder fica marcado para ligares.
          </p>
        </div>
        <p v-if="original && original.isTest" class="callout"><b-icon icon="flask" size="is-small" />Tour de teste: as mensagens vão só para o grupo de testes.</p>

        <!-- 1. When -->
        <section class="co-card block" :class="{ 'has-error': errors.when }">
          <h2 class="step"><span class="step-n">1</span>Quando</h2>
          <div class="grid-2">
            <label class="field-ui">
              <span>Data</span>
              <input v-model="form.date" type="date" :min="today" @input="errors.when = ''" />
            </label>
            <label class="field-ui">
              <span>Hora de início</span>
              <input v-model="form.startTime" type="time" step="300" @input="errors.when = ''" />
            </label>
          </div>
          <p v-if="errors.when" ref="err-when" class="field-error" role="alert">{{ errors.when }}</p>
        </section>

        <!-- 2. Where -->
        <section class="co-card block" :class="{ 'has-error': errors.pickup || errors.dropoff }">
          <h2 class="step"><span class="step-n">2</span>Onde</h2>

          <p class="group-label">Pick-up</p>
          <div class="chips" role="radiogroup" aria-label="Pick-up">
            <button
              v-for="loc in pickupOptions"
              :key="loc.id"
              type="button"
              role="radio"
              :aria-checked="pickupChoice === loc.id"
              :class="{ 'is-on': pickupChoice === loc.id }"
              @click="pickupChoice = loc.id; errors.pickup = ''"
            >
              {{ loc.label }}
            </button>
          </div>
          <label v-if="pickupChoice === 'other'" class="field-ui mt">
            <span>Local de pick-up</span>
            <input v-model.trim="pickupOther" type="text" maxlength="120" placeholder="Ex.: Hotel Tivoli Avenida" @input="errors.pickup = ''" />
          </label>
          <p v-if="errors.pickup" ref="err-pickup" class="field-error" role="alert">{{ errors.pickup }}</p>

          <p class="group-label">Drop-off <span>opcional</span></p>
          <div class="chips" role="radiogroup" aria-label="Drop-off">
            <button
              v-for="loc in dropoffOptions"
              :key="loc.id"
              type="button"
              role="radio"
              :aria-checked="dropoffChoice === loc.id"
              :class="{ 'is-on': dropoffChoice === loc.id }"
              @click="dropoffChoice = loc.id; errors.dropoff = ''"
            >
              {{ loc.label }}
            </button>
          </div>
          <label v-if="dropoffChoice === 'other'" class="field-ui mt">
            <span>Local de drop-off</span>
            <input v-model.trim="dropoffOther" type="text" maxlength="120" placeholder="Escreve o local" @input="errors.dropoff = ''" />
          </label>
          <p v-if="errors.dropoff" class="field-error" role="alert">{{ errors.dropoff }}</p>

          <label v-if="pickupChoice === 'other' || dropoffChoice === 'other'" class="check">
            <input v-model="saveLocation" type="checkbox" />
            <span>Guardar este local para os próximos pedidos</span>
          </label>
        </section>

        <!-- 3. Tuk-tuks and pay -->
        <section class="co-card block">
          <h2 class="step"><span class="step-n">3</span>Tuk-tuks e pagamento</h2>
          <div class="grid-2">
            <div class="field-ui">
              <span>Tuk-tuks</span>
              <div class="stepper">
                <button type="button" aria-label="Menos um tuk-tuk" :disabled="form.tuksTotal <= 1" @click="step('tuksTotal', -1)">−</button>
                <output aria-live="polite">{{ form.tuksTotal }}</output>
                <button type="button" aria-label="Mais um tuk-tuk" @click="step('tuksTotal', 1)">+</button>
              </div>
            </div>
            <div class="field-ui">
              <span>Destes, de 6 lugares</span>
              <div class="stepper">
                <button type="button" aria-label="Menos um de 6 lugares" :disabled="form.tuksSixSeatsRequired <= 0" @click="step('tuksSixSeatsRequired', -1)">−</button>
                <output aria-live="polite">{{ form.tuksSixSeatsRequired }}</output>
                <button type="button" aria-label="Mais um de 6 lugares" :disabled="form.tuksSixSeatsRequired >= form.tuksTotal" @click="step('tuksSixSeatsRequired', 1)">+</button>
              </div>
              <small>{{ form.tuksSixSeatsRequired ? `Os outros ${form.tuksTotal - form.tuksSixSeatsRequired} podem ser de 4 ou 6` : "Qualquer tuk serve" }}</small>
            </div>
            <label class="field-ui">
              <span>Pagamento por guia</span>
              <span class="money">
                <input v-model="form.guidePay" type="number" min="0" step="0.5" inputmode="decimal" placeholder="45" />
                <span class="unit">€</span>
              </span>
              <small>Aparece no anúncio. Vazio = «a confirmar».</small>
            </label>
          </div>
        </section>

        <!-- 4. Client (admins only) -->
        <section class="co-card block">
          <h2 class="step">
            <span class="step-n">4</span>Cliente
            <span class="lock"><b-icon icon="lock" size="is-small" />Os guias não veem</span>
          </h2>
          <div class="grid-2">
            <label class="field-ui">
              <span>WhatsApp de quem pediu</span>
              <input v-model.trim="form.clientWhatsapp" type="tel" placeholder="+82 10 5521 4471" />
            </label>
          </div>
          <label class="field-ui mt">
            <span>Notas internas <em>(opcional)</em></span>
            <textarea v-model.trim="form.internalNotes" rows="3" maxlength="1000" placeholder="Ex.: grupo com crianças, pagar em dinheiro…"></textarea>
          </label>
        </section>

        <!-- 5. When the group hears about it (new tours and drafts) -->
        <section v-if="canDraft" class="co-card block" :class="{ 'has-error': errors.announce }">
          <h2 class="step"><span class="step-n">5</span>Anúncio no grupo</h2>
          <div class="chips" role="radiogroup" aria-label="Quando anunciar">
            <button type="button" role="radio" :aria-checked="!scheduled" :class="{ 'is-on': !scheduled }" @click="announce = 'now'; errors.announce = ''">
              Quando eu publicar
            </button>
            <button type="button" role="radio" :aria-checked="scheduled" :class="{ 'is-on': scheduled }" @click="announce = 'later'">Agendar</button>
          </div>
          <template v-if="scheduled">
            <div class="grid-2 mt">
              <label class="field-ui">
                <span>Data do anúncio</span>
                <input v-model="publishDate" type="date" :min="today" :max="form.date || null" @input="errors.announce = ''" />
              </label>
              <label class="field-ui">
                <span>Hora do anúncio</span>
                <input v-model="publishTime" type="time" step="300" @input="errors.announce = ''" />
              </label>
            </div>
            <p class="hint">Até lá fica em rascunho: podes alterar ou cancelar o tour e ninguém é avisado.</p>
          </template>
          <p v-if="errors.announce" class="field-error" role="alert">{{ errors.announce }}</p>
        </section>

        <!-- Rarely used: collapsed -->
        <details v-if="!editId" class="co-card block advanced">
          <summary>Mais opções</summary>
          <label class="check">
            <input v-model="form.isTest" type="checkbox" />
            <span>
              <strong>Tour de teste</strong>
              <small>As mensagens vão só para o grupo de testes e os alertas são marcados 🧪 TESTE. Os guias não veem este tour.</small>
            </span>
          </label>
        </details>

        <!-- Phones: the announcement under the form -->
        <details class="co-card block preview-inline">
          <summary>Ver o anúncio do Telegram</summary>
          <p class="co-message">{{ preview }}</p>
        </details>

        <p v-if="formError" class="form-error" role="alert">{{ formError }}</p>

        <!-- Actions stay in reach -->
        <div class="actions">
          <router-link :to="editId ? { name: 'coreanos-detail', params: { id: editId } } : { name: 'coreanos' }" class="link">Cancelar</router-link>
          <div class="actions-main">
            <button v-if="canDraft && !scheduled" type="button" class="co-btn" :disabled="!!busy" @click="submit(false)">{{ busy === "draft" ? "A guardar…" : "Guardar rascunho" }}</button>
            <button type="submit" class="co-btn is-lime" :disabled="!!busy">{{ primaryLabel }}</button>
          </div>
        </div>
      </form>

      <aside class="side">
        <div class="co-dark preview">
          <p class="co-label lime">Anúncio no Telegram</p>
          <p class="co-message">{{ preview }}</p>
          <p class="side-note">
            {{ sideNote }}
          </p>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import { listLocations, saveTourRequest, publishTourRequest, scheduleTourRequest, getAdminData, newTourMessage, dayLabel } from "@/services/coreanos";

const lisbonToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Lisbon" }).format(new Date());
const lisbonNowTime = () => new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Lisbon", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date());
// ISO → ["2026-11-05", "09:00"] in Lisbon
const lisbonParts = (iso) => [
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Lisbon" }).format(new Date(iso)),
  new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Lisbon", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso)),
];

export default {
  name: "CoreanosForm",
  metaInfo() {
    return { title: `${this.editId ? "Editar pedido" : "Novo pedido"} | Pedidos Coreanos` };
  },
  data() {
    return {
      loading: true,
      locations: [],
      original: null,
      form: { date: "", startTime: "09:30", tuksTotal: 1, tuksSixSeatsRequired: 0, guidePay: "", isTest: false, clientWhatsapp: "", internalNotes: "" },
      pickupChoice: null,
      dropoffChoice: "none",
      pickupOther: "",
      dropoffOther: "",
      saveLocation: false,
      busy: "",
      errors: { when: "", pickup: "", dropoff: "", announce: "" },
      formError: "",
      // "now" = when the admin taps Publicar; "later" = the server publishes at publishDate/publishTime
      announce: this.$route.query.agendar ? "later" : "now",
      publishDate: lisbonToday(),
      publishTime: "",
      today: lisbonToday(),
    };
  },
  computed: {
    editId() {
      return this.$route.params.id || null;
    },
    // Editing a tour the guides already see (not a draft)
    isLiveEdit() {
      return !!(this.editId && this.original && this.original.status !== "draft");
    },
    // New tour or a draft: "Guardar rascunho" + "Publicar"
    canDraft() {
      return !this.isLiveEdit;
    },
    primaryPublishes() {
      return this.canDraft;
    },
    scheduled() {
      return this.canDraft && this.announce === "later";
    },
    primaryLabel() {
      if (this.busy === "publish") return "A publicar…";
      if (this.busy === "schedule") return "A agendar…";
      if (this.busy === "save") return "A guardar…";
      if (this.scheduled) return this.publishDate && this.publishTime ? `Agendar para ${dayLabel(this.publishDate)} ${this.publishTime}` : "Agendar anúncio";
      return this.canDraft ? "Publicar no grupo" : "Guardar alterações";
    },
    sideNote() {
      if (this.isLiveEdit) return "O grupo recebe as alterações com o antes e o depois.";
      if (this.scheduled) return "Vai para o grupo dos guias à hora agendada e começam 30 min para responderem. Até lá ninguém é avisado.";
      return "Ao publicar vai para o grupo dos guias e começam 30 min para responderem.";
    },
    assignedCount() {
      return (this.original && this.original.counts && this.original.counts.assigned) || 0;
    },
    needsReconfirm() {
      return this.isLiveEdit && this.assignedCount > 0 && (this.form.date !== this.original.date || this.form.startTime !== this.original.startTime);
    },
    // New tour, or date/time changed, at or before now (Lisbon). Same check on the server.
    startsInPast() {
      const { date, startTime } = this.form;
      if (!date || !startTime) return false;
      if (this.original && this.original.date === date && this.original.startTime === startTime) return false;
      const today = lisbonToday();
      return date < today || (date === today && startTime <= lisbonNowTime());
    },
    pickupOptions() {
      return [...this.locations.filter((l) => l.usableAs !== "dropoff"), { id: "other", label: "Outro local…" }];
    },
    dropoffOptions() {
      return [{ id: "none", label: "Sem drop-off" }, ...this.locations.filter((l) => l.usableAs !== "pickup"), { id: "other", label: "Outro local…" }];
    },
    pickup() {
      if (this.pickupChoice === "other") return this.pickupOther ? { presetId: null, label: this.pickupOther } : null;
      const loc = this.locations.find((l) => l.id === this.pickupChoice);
      return loc ? { presetId: loc.id, label: loc.label } : null;
    },
    dropoff() {
      if (this.dropoffChoice === "none") return null;
      if (this.dropoffChoice === "other") return this.dropoffOther ? { presetId: null, label: this.dropoffOther } : null;
      const loc = this.locations.find((l) => l.id === this.dropoffChoice);
      return loc ? { presetId: loc.id, label: loc.label } : null;
    },
    preview() {
      return newTourMessage({
        ...this.form,
        date: this.form.date || this.today,
        pickup: this.pickup || { label: "(escolhe o pick-up)" },
        dropoff: this.dropoff,
        shortId: this.original && this.original.shortId,
      });
    },
  },
  async mounted() {
    try {
      this.locations = await listLocations();
      if (this.editId) await this.loadRequest();
      else {
        this.form.date = this.today;
        this.pickupChoice = (this.pickupOptions[0] || {}).id;
      }
    } catch (error) {
      this.formError = "Não foi possível carregar o formulário.";
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadRequest() {
      const data = await getAdminData(this.editId);
      const r = data.request;
      const priv = data.private || {};
      this.original = r;
      this.form = {
        date: r.date,
        startTime: r.startTime,
        tuksTotal: r.tuksTotal,
        tuksSixSeatsRequired: r.tuksSixSeatsRequired || 0,
        guidePay: r.guidePay == null ? "" : r.guidePay,
        isTest: !!r.isTest,
        clientWhatsapp: priv.clientWhatsapp || "",
        internalNotes: priv.internalNotes || "",
      };
      const pick = (p, fallback) => {
        if (!p) return [fallback, ""];
        const preset = this.locations.find((l) => l.id === p.presetId || l.label === p.label);
        return preset ? [preset.id, ""] : ["other", p.label];
      };
      [this.pickupChoice, this.pickupOther] = pick(r.pickup, "other");
      [this.dropoffChoice, this.dropoffOther] = pick(r.dropoff, "none");
      if (r.publishAt) {
        this.announce = "later";
        [this.publishDate, this.publishTime] = lisbonParts(r.publishAt);
      }
    },

    step(field, delta) {
      const next = this.form[field] + delta;
      if (field === "tuksTotal") {
        this.form.tuksTotal = Math.min(60, Math.max(1, next));
        this.form.tuksSixSeatsRequired = Math.min(this.form.tuksSixSeatsRequired, this.form.tuksTotal);
      } else {
        this.form.tuksSixSeatsRequired = Math.min(this.form.tuksTotal, Math.max(0, next));
      }
    },

    // Errors next to their field; the first one is scrolled into view
    validate() {
      const e = { when: "", pickup: "", dropoff: "", announce: "" };
      if (!this.form.date || !this.form.startTime) e.when = "Escolhe a data e a hora.";
      else if (this.startsInPast) e.when = "Esta data e hora já passaram. Escolhe uma futura.";
      if (!this.pickup) e.pickup = this.pickupChoice === "other" ? "Escreve o local de pick-up." : "Escolhe o pick-up.";
      if (this.dropoffChoice === "other" && !this.dropoff) e.dropoff = "Escreve o local de drop-off.";
      if (this.scheduled) {
        // Lisbon "YYYY-MM-DD HH:MM" strings compare in time order
        const at = `${this.publishDate} ${this.publishTime}`;
        if (!this.publishDate || !this.publishTime) e.announce = "Escolhe a data e a hora do anúncio.";
        else if (at <= `${lisbonToday()} ${lisbonNowTime()}`) e.announce = "Essa hora já passou. Escolhe uma hora futura.";
        else if (at >= `${this.form.date} ${this.form.startTime}`) e.announce = "O anúncio tem de sair antes do início do tour.";
      }
      this.errors = e;
      const first = ["when", "pickup", "dropoff", "announce"].find((k) => e[k]);
      if (first) {
        this.$nextTick(() => {
          const el = this.$el.querySelector(".has-error");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      }
      return !first;
    },

    async submit(publish) {
      this.formError = "";
      if (!this.validate()) return;

      const schedule = publish && this.scheduled;
      this.busy = schedule ? "schedule" : publish ? "publish" : this.canDraft ? "draft" : "save";
      try {
        const saved = await saveTourRequest({
          id: this.editId,
          ...this.form,
          pickup: this.pickup,
          dropoff: this.dropoff,
          saveLocation: this.saveLocation,
        });
        const id = saved.id;
        if (schedule) await scheduleTourRequest(id, this.publishDate, this.publishTime);
        // "Guardar rascunho" with "Quando eu publicar": drop an earlier schedule
        else if (!publish && this.canDraft && this.original && this.original.publishAt) await scheduleTourRequest(id);
        const sent = publish && !schedule ? (await publishTourRequest(id)).telegram : saved.telegram;
        if (sent && !sent.sent) {
          this.$buefy.toast.open({ message: `Guardado, mas a mensagem não foi enviada para o Telegram: ${sent.error}`, type: "is-warning", duration: 8000, position: "is-bottom" });
        }
        const done = schedule ? `Agendado para ${dayLabel(this.publishDate)} ${this.publishTime}.` : publish ? "Pedido publicado." : "Guardado.";
        this.$buefy.toast.open({ message: done, type: "is-success", position: "is-bottom" });
        this.$router.push({ name: "coreanos-detail", params: { id } });
      } catch (error) {
        this.formError = error.message;
      } finally {
        this.busy = "";
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "./coreanos";

.back {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: $ink-sub;

  &:hover {
    color: $forest;
  }
}

.lead {
  margin: 0.25rem 0 1.5rem;
  color: $ink-sub;
}

.loading {
  position: relative;
  min-height: 12rem;
}

.co-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 20rem;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
}

.main {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.callout {
  display: flex;
  gap: 0.625rem;
  align-items: flex-start;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  background: $soft;
  font-size: 0.9375rem;
  color: $ink;

  &.is-warn {
    background: $warn-soft;
    color: $warn-ink;
  }

  .icon {
    margin-top: 0.125rem;
  }
}

.block {
  padding: 1.25rem 1.375rem;

  &.has-error {
    border-color: #f0c9c2;
  }

  @media (max-width: 600px) {
    padding: 1.125rem 1rem;
  }
}

.step {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 1rem;
  font-family: $display;
  font-size: 1.0625rem;
  font-weight: 600;
  color: $forest;
}

.step-n {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  background: $forest;
  font-size: 0.75rem;
  color: $lime;
}

.lock {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 500;
  color: $ink-faint;
}

.grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 1rem;
}

.mt {
  margin-top: 0.75rem;
}

.field-ui {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: $ink-sub;

  em {
    font-style: normal;
    font-weight: 400;
    color: $ink-faint;
  }

  small {
    font-size: 0.8125rem;
    font-weight: 400;
    color: $ink-faint;
  }

  input,
  textarea {
    width: 100%;
    padding: 0 0.875rem;
    border: 1px solid $line;
    border-radius: 10px;
    background: #fff;
    font: inherit;
    font-size: 1rem; // 16px: no zoom on iPhone
    font-weight: 400;
    color: $ink;

    &:focus {
      border-color: $forest;
      outline: 0;
      box-shadow: 0 0 0 3px rgba(44, 110, 87, 0.15);
    }
  }

  input {
    height: 2.75rem;
  }

  textarea {
    padding: 0.75rem 0.875rem;
    resize: vertical;
  }
}

.money {
  position: relative;

  input {
    padding-right: 2rem;
  }

  .unit {
    position: absolute;
    top: 50%;
    right: 0.875rem;
    transform: translateY(-50%);
    color: $ink-faint;
  }
}

.stepper {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  border: 1px solid $line;
  border-radius: 10px;
  overflow: hidden;

  button {
    width: 2.75rem;
    height: 2.75rem;
    border: 0;
    background: #fff;
    font-size: 1.25rem;
    color: $forest;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: $soft;
    }

    &:disabled {
      color: $ink-faint;
      cursor: default;
    }
  }

  output {
    min-width: 3rem;
    font-family: $display;
    font-size: 1.125rem;
    font-weight: 700;
    text-align: center;
    color: $forest;
  }
}

.group-label {
  margin: 0 0 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: $ink-sub;

  & ~ .group-label {
    margin-top: 1.25rem;
  }

  span {
    font-weight: 400;
    color: $ink-faint;
  }
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  button {
    min-height: 2.5rem;
    padding: 0 1rem;
    border: 1px solid $line;
    border-radius: 999px;
    background: #fff;
    font: inherit;
    font-size: 0.9375rem;
    color: $ink;
    cursor: pointer;

    &:hover {
      border-color: $forest-2;
    }

    &.is-on {
      border-color: $forest;
      background: $forest;
      color: #fff;
    }
  }
}

.check {
  display: flex;
  gap: 0.625rem;
  align-items: flex-start;
  margin-top: 1rem;
  font-size: 0.9375rem;
  color: $ink;
  cursor: pointer;

  input {
    width: 1.125rem;
    height: 1.125rem;
    margin-top: 0.125rem;
    accent-color: $forest;
  }

  span {
    display: flex;
    flex-direction: column;
  }

  small {
    font-size: 0.8125rem;
    color: $ink-sub;
  }
}

.hint {
  margin-top: 0.625rem;
  font-size: 0.8125rem;
  color: $ink-sub;
}

.field-error {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #a32c1f;
}

.form-error {
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: #fbe4e1;
  color: #a32c1f;
}

details.block summary {
  font-weight: 600;
  color: $forest;
  cursor: pointer;
}

.advanced .check {
  margin-top: 0.875rem;
}

// Desktop shows the announcement on the side; phones get it here
.preview-inline {
  display: none;

  .co-message {
    margin-top: 0.75rem;
    background: $soft;
  }

  @media (max-width: 960px) {
    display: block;
  }
}

// Sticky at the bottom: the main action is always in reach
.actions {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0 -0.25rem;
  padding: 0.875rem 0.25rem calc(0.875rem + env(safe-area-inset-bottom));
  border-top: 1px solid $line;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);

  .link {
    font-size: 0.9375rem;
    font-weight: 500;
    color: $ink-sub;
    text-decoration: underline;
  }

  @media (max-width: 600px) {
    // Main action on top, full width (thumb reach)
    .actions-main {
      flex-direction: column-reverse;
      width: 100%;
    }

    .co-btn {
      width: 100%;
    }

    .link {
      order: 2;
      width: 100%;
      text-align: center;
    }
  }
}

.actions-main {
  display: flex;
  gap: 0.5rem;
}

.side {
  position: sticky;
  top: 6rem;

  @media (max-width: 960px) {
    display: none;
  }
}

.preview {
  padding: 1.25rem;

  .lime {
    margin-bottom: 0.75rem;
    color: $lime;
  }
}

.side-note {
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.75);
}
</style>
